import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { handleApiError, usersApi } from '../lib/api-client';
import type { AssignLocationDto, CreateUserDto, UpdateUserDto, UpdateUserStatusDto } from '../types/api';

export const useUsers = (params?: {
  status?: 'ACTIVE' | 'INACTIVE';
  search?: string;
  roleId?: string;
  geoLocationId?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => usersApi.getAll(params),
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => usersApi.getById(id),
    enabled: !!id,
  });
};

export const useApproveUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => usersApi.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User approved successfully');
    },
    onError: handleApiError,
  });
};

export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserStatusDto }) => usersApi.updateStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User status updated successfully');
    },
    onError: handleApiError,
  });
};

export const useAssignLocations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignLocationDto }) => usersApi.assignLocations(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Locations assigned successfully');
    },
    onError: handleApiError,
  });
};

export const useUserLocations = (id: string) => {
  return useQuery({
    queryKey: ['users', id, 'locations'],
    queryFn: () => usersApi.getAssignedLocations(id),
    enabled: !!id,
  });
};

export const useUpdateUserLocations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignLocationDto }) => usersApi.updateLocations(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', id, 'locations'] });
      toast.success('User locations updated successfully');
    },
    onError: handleApiError,
  });
};

export const useRemoveUserLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, geoLocationId }: { userId: string; geoLocationId: string }) =>
      usersApi.removeLocation(userId, geoLocationId),
    onSuccess: (updatedList, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'locations'] });
      toast.success('Location removed');
    },
    onError: handleApiError,
  });
};

export const useRejectUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => usersApi.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User rejected successfully');
    },
    onError: handleApiError,
  });
};

export const useDeactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => usersApi.updateStatus(id, { status: 'INACTIVE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deactivated successfully');
    },
    onError: handleApiError,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) => usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated successfully');
    },
    onError: handleApiError,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserDto) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created successfully');
    },
    onError: handleApiError,
  });
};

export const useDownloadOnboardingTemplate = () => {
  return useMutation({
    mutationFn: () => usersApi.downloadOnboardingTemplate(),
    onError: handleApiError,
  });
};

export const useBulkImportUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => usersApi.bulkImport(file),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (result.created.length > 0) {
        toast.success(`${result.created.length} user(s) imported successfully`);
      }
    },
    onError: handleApiError,
  });
};

export const useDownloadBulkTransferLocationsTemplate = () => {
  return useMutation({
    mutationFn: () => usersApi.downloadBulkTransferLocationsTemplate(),
    onError: handleApiError,
  });
};

export const useBulkTransferLocations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) => usersApi.bulkTransferLocations(file, dryRun),
    onSuccess: (result) => {
      if (!result.dryRun && result.summary.ok > 0) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        toast.success(`${result.summary.ok} user(s) transferred successfully`);
      }
    },
    onError: handleApiError,
  });
};

export const useBulkToggleCentralLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emails, isCentralLevel }: { emails: string[]; isCentralLevel: boolean }) =>
      usersApi.bulkToggleCentralLevel(emails, isCentralLevel),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`Central-level access ${result.isCentralLevel ? 'granted' : 'revoked'} for ${result.updated} user(s)`);
    },
    onError: handleApiError,
  });
};
